import { visit } from "unist-util-visit";
import type { Plugin } from "unified";
import type { Processor } from "unified";
import type { Root, Text, PhrasingContent } from "mdast";

const DELIM_TO_TYPE = {
  "*": "strong",
  "_": "emphasis",
  "~": "delete",
} as const;

// Three per-delimiter patterns. Each:
//   - negative lookbehind for the same delimiter (prevents ** or __ matching)
//   - positive lookbehind for start-of-string or whitespace/punctuation
//   - inner content excludes only the same delimiter (allows the other two for nesting)
//   - lookahead: end-of-string or whitespace/punctuation
const PATTERN_STAR = /(?<![*])(?<=^|[\s\p{P}])(\*)([^\s\n*][^*\n]*?[^\s\n*]|[^\s\n*])\1(?=$|[\s\p{P}])/u;
const PATTERN_UNDER = /(?<![_])(?<=^|[\s\p{P}])(_)([^\s\n_][^_\n]*?[^\s\n_]|[^\s\n_])\1(?=$|[\s\p{P}])/u;
const PATTERN_TILDE = /(?<![~])(?<=^|[\s\p{P}])(~)([^\s\n~][^~\n]*?[^\s\n~]|[^\s\n~])\1(?=$|[\s\p{P}])/u;

// exec() liefert RegExpExecArray (index und input sind dort zugesichert) —
// nicht RegExpMatchArray, wo beide optional sind. Mit dem falschen Typ im
// Prädikat greift die Einengung nicht, und index bleibt fragwürdig.
function findEarliestMatch(text: string): RegExpExecArray | null {
  const candidates = [
    PATTERN_STAR.exec(text),
    PATTERN_UNDER.exec(text),
    PATTERN_TILDE.exec(text),
  ].filter((m): m is RegExpExecArray => m !== null);
  if (candidates.length === 0) return null;
  // Pick the match that starts earliest; on tie, arbitrary order is fine
  return candidates.reduce((best, cur) => (cur.index < best.index ? cur : best));
}

function splitText(node: Text): PhrasingContent[] | null {
  let text = node.value;
  const out: PhrasingContent[] = [];
  let m: RegExpMatchArray | null;
  while ((m = findEarliestMatch(text)) !== null) {
    const before = text.slice(0, m.index!);
    const delim = m[1]! as keyof typeof DELIM_TO_TYPE;
    const inner = m[2]!;
    const after = text.slice(m.index! + m[0].length);
    if (before) out.push({ type: "text", value: before });
    const innerNodes = splitText({ type: "text", value: inner }) ?? [
      { type: "text", value: inner } as Text,
    ];
    out.push({ type: DELIM_TO_TYPE[delim], children: innerNodes } as PhrasingContent);
    text = after;
  }
  if (out.length === 0) return null;
  if (text) out.push({ type: "text", value: text });
  return out;
}

export const remarkWhatsappEmphasis: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "text", (node: Text, index, parent: any) => {
      if (index == null || parent == null) return;
      if (parent.type === "code" || parent.type === "inlineCode") return;
      const split = splitText(node);
      if (!split) return;
      parent.children.splice(index, 1, ...split);
    });
  };
};

export const remarkDisableAttention: Plugin<[], Root> = function () {
  const data = (this as unknown as Processor).data() as any;
  const list = (data.micromarkExtensions ??= []);
  list.push({ disable: { null: ["attention"] } });
};
